<script setup lang="ts" generic="T">
import type { EmblaOptionsType, EmblaPluginType } from 'embla-carousel'
import emblaCarouselVue from 'embla-carousel-vue'
import type { HTMLAttributes } from 'vue'

const props = defineProps<{
  class?: HTMLAttributes['class']
  classContent?: HTMLAttributes['class']
  classContentWrapper?: HTMLAttributes['class']
  classContentContainer?: HTMLAttributes['class']
  options?: MaybeRef<EmblaOptionsType>
  plugins?: MaybeRef<EmblaPluginType[]>
  items: T[]
}>()

const [carouselContentRef, carouselContentApi] = emblaCarouselVue(
  props.options,
  props.plugins
)

const selectedIndex = ref(0)
const isDisabledPrevButton = ref(true)
const isDisabledNextButton = ref(true)

const scrollSnapList = computed(() => {
  if (!carouselContentApi.value) return []
  return carouselContentApi.value.scrollSnapList()
})

const onPrevButtonClick = () => {
  if (!carouselContentApi.value) return
  carouselContentApi.value.scrollPrev()
}

const onNextButtonClick = () => {
  if (!carouselContentApi.value) return
  carouselContentApi.value.scrollNext()
}

const onSelect = () => {
  if (!carouselContentApi.value) return
  selectedIndex.value = carouselContentApi.value.selectedScrollSnap()
  isDisabledPrevButton.value = !carouselContentApi.value.canScrollPrev()
  isDisabledNextButton.value = !carouselContentApi.value.canScrollNext()
}

const initListener = () => {
  if (!carouselContentApi.value) return

  carouselContentApi.value
    .on('init', onSelect)
    .on('reInit', onSelect)
    .on('select', onSelect)
}

onMounted(() => {
  initListener()
})

defineExpose({
  carouselContentRef,
  carouselContentApi,
  selectedIndex,
  isDisabledPrevButton,
  isDisabledNextButton,
  scrollSnapList,
  onPrevButtonClick,
  onNextButtonClick
})
</script>

<template>
  <section :class="cn('w-full flex flex-col gap-2', props.class)">
    <div
      ref="carouselContentRef"
      :class="cn('overflow-hidden', props.classContentWrapper)"
    >
      <div :class="cn('flex gap-2', props.classContentContainer)">
        <div
          v-for="(item, index) in items"
          :key="index"
          :class="cn('grow-0 shrink-0 basis-full', props.classContent)"
        >
          <slot name="content" :item="item" :index="index">
            <div
              class="bg-outline flex justify-center items-center py-32 select-none"
            >
              <p class="text-7xl font-bold">{{ index + 1 }}</p>
            </div>
          </slot>
        </div>
      </div>
    </div>

    <slot
      :is-disabled-prev-button="isDisabledPrevButton"
      :is-disabled-next-button="isDisabledNextButton"
      :on-prev-button-click="onPrevButtonClick"
      :on-next-button-click="onNextButtonClick"
      :scroll-snap-list="scrollSnapList"
      :selected-index="selectedIndex"
    />
  </section>
</template>
